import {
  BackText,
  ButtonView,
  ButtonsContainer,
  ButtonsWrapper,
  CheckboxWrapper,
  CookieBody,
  CookieItem,
  CookieItemWrapper,
  FooterLink,
  GdprRoot,
  LeftSideBox,
  PermissionView,
  SmallText,
  Text,
  Title,
  ToggleWrapper,
} from './styles';
import { CenterItem, Column, RowAlignedItems } from '@components/LinearLayout';
import { cookieGdprName, setCookie } from '@utils/Inbound/inboundHelper';
import {
  gdprConfigProps,
  getGdprCookie,
  getIsGdprOpen,
  setIsGdprOpen,
  setGdprCookie,
} from '@store/slices/configSlice';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import Divider from '@components/Divider';
import ExpandableCard from '@components/ExpandableCard';
import { RegularText } from '@components/ExpandableCard/styles';
import Toggle from '@components/Toggle';
import { truncateAfterLastBrace } from '@utils/fn';
import { useMobileWidth } from '@utils/hooks';
import { useSpring } from '@react-spring/web';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { APP_HOST } from '@utils/apiHooks';

export const defaultGdprConfig = {
  essential: true,
  marketing: { googleAnalytics: false, mailTracking: false },
  externalMedia: { youtube: false, vimeo: false },
};

export const acceptGdprConfig = {
  essential: true,
  marketing: { googleAnalytics: true, mailTracking: true },
  externalMedia: { youtube: true, vimeo: true },
};

const combineOrBooleanValues = (array: boolean[]) =>
  array.reduce((acc, curr) => acc || curr, false);

const Gdpr = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMobileWidth();
  const [expanded, setExpanded] = useState<boolean>(false);
  const isGdprOpen = useSelector(getIsGdprOpen);
  const [showGdprDetails, setShowGdprDetails] = useState<boolean>(false);
  const gdprCookie = useSelector(getGdprCookie);

  const safeJSONParse = (value: string | null | undefined) => {
    if (!value || value.trim() === '' || typeof value !== 'string') {
      return defaultGdprConfig;
    }
    try {
      return JSON.parse(value.trim());
    } catch (error) {
      console.error('Error parsing JSON:', error, 'Value:', value);
      return defaultGdprConfig;
    }
  };

  const gdprConfig: gdprConfigProps = gdprCookie
    ? safeJSONParse(truncateAfterLastBrace(gdprCookie))
    : defaultGdprConfig;

  const [gdprState, setGdprState] = useState<gdprConfigProps>(gdprConfig);
  const [expandedCard, setExpandedCard] = useState<string>('');

  const bodyProps = useSpring({
    from: { maxHeight: '60vh' },
    to: { maxHeight: '2rem' },
    config: { duration: 700 },
    reverse: !expanded,
  });

  const onBackClick = useCallback(() => {
    setExpanded(prevState => !prevState);
    setTimeout(() => {
      setExpanded(prevState => !prevState);
      setShowGdprDetails(false);
    }, 900);
  }, []);

  const onDetailsClick = useCallback(() => {
    setExpanded(prevState => !prevState);
    setTimeout(() => {
      setExpanded(prevState => !prevState);
      setShowGdprDetails(true);
    }, 900);
  }, []);

  const acceptAllGdprCookies = useCallback(() => {
    setCookie(cookieGdprName, JSON.stringify(acceptGdprConfig), 30);
    dispatch(setGdprCookie(JSON.stringify(acceptGdprConfig)));
    dispatch(setIsGdprOpen(false));
  }, [dispatch]);

  const onSaveGdprCookies = useCallback(() => {
    setCookie(cookieGdprName, JSON.stringify(gdprState), 30);
    dispatch(setGdprCookie(JSON.stringify(gdprState)));
    dispatch(setIsGdprOpen(false));
  }, [dispatch, gdprState]);

  const buttonItems = useMemo(
    () => [
      {
        label: 'accept-all',
        backgroundColor: theme.color.gdprGrey2,
        hoverTextColor: theme.color.white,
        hoverBackgroundColor: '#a3a7ae',
        textColor: theme.color.white,
        onClick: acceptAllGdprCookies,
      },
      {
        label: 'save',
        backgroundColor: '#f3f4f5',
        hoverTextColor: theme.color.gdprGrey2,
        hoverBackgroundColor: theme.color.gdprGrey,
        textColor: theme.color.gdprGrey2,
        onClick: onSaveGdprCookies,
      },
      {
        label: 'individual-data-protection-preferences',
        backgroundColor: theme.color.black,
        textColor: theme.color.white,
        hoverBackgroundColor: '#262626',
        onClick: onDetailsClick,
      },
    ],
    [
      acceptAllGdprCookies,
      onDetailsClick,
      onSaveGdprCookies,
      theme.color.black,
      theme.color.gdprGrey,
      theme.color.gdprGrey2,
      theme.color.white,
    ]
  );
  const gdprItemsCookies = useMemo(
    () => ({
      essential: [
        {
          id: 'google-tag-manager',
          details: [
            {
              subtitle: t('name'),
              element: <RegularText>{t('google-tag-manager')}</RegularText>,
            },
            {
              subtitle: t('provider'),
              element: (
                <RegularText>
                  Google Ireland Limited, Gordon House, Barrow Street, Dublin 4,
                  Ireland
                </RegularText>
              ),
            },
            {
              subtitle: t('purpose'),
              element: (
                <RegularText>{t('google-tag-manager-purpose')}</RegularText>
              ),
            },
            {
              subtitle: t('privacy-policy'),
              element: (
                <RegularText>
                  https://policies.google.com/privacy?hl=en
                </RegularText>
              ),
            },
            {
              subtitle: t('cookie-name'),
              element: <RegularText>_ga,_gat,_gid</RegularText>,
            },
            {
              subtitle: t('cookie-expiration'),
              element: (
                <RegularText>{t('google-tag-manager-duration')}</RegularText>
              ),
            },
          ],
        },

        {
          id: 'registering',
          details: [
            {
              subtitle: t('name'),
              element: <RegularText>{t('registering')}</RegularText>,
            },
            {
              subtitle: t('provider'),
              element: <RegularText>{t('website-operator')}</RegularText>,
            },
            {
              subtitle: t('purpose'),
              element: <RegularText>{t('registering-purpose')}</RegularText>,
            },
            {
              subtitle: t('privacy-policy'),
              element: (
                <RegularText>
                  {`${APP_HOST}${t('mappable-company-routes.privacy-policy-url')}`}
                </RegularText>
              ),
            },
            {
              subtitle: t('cookie-name'),
              element: <RegularText>qq33d1ad3aggtoe</RegularText>,
            },
            {
              subtitle: t('cookie-expiration'),
              element: (
                <RegularText>{t('google-tag-manager-duration')}</RegularText>
              ),
            },
          ],
        },
      ],
      marketing: [
        {
          id: 'google-analytics',
          details: [
            {
              subtitle: t('accept'),
              element: (
                <ToggleWrapper>
                  <Toggle
                    id="google-analytics"
                    value={gdprState?.marketing?.googleAnalytics}
                    onChange={() => {
                      setGdprState(prevState => ({
                        ...prevState,
                        marketing: {
                          googleAnalytics:
                            !gdprState?.marketing?.googleAnalytics,
                          mailTracking: gdprState?.marketing?.mailTracking,
                        },
                      }));
                    }}
                  />
                  <RegularText>
                    {gdprState?.marketing?.googleAnalytics ? t('on') : t('off')}
                  </RegularText>
                </ToggleWrapper>
              ),
            },
            {
              subtitle: t('name'),
              element: <RegularText>{t('google-analytics')}</RegularText>,
            },
            {
              subtitle: t('provider'),
              element: (
                <RegularText>
                  Google Ireland Limited, Gordon House, Barrow Street, Dublin 4,
                  Ireland
                </RegularText>
              ),
            },
            {
              subtitle: t('purpose'),
              element: (
                <RegularText>{t('google-analytics-purpose')}</RegularText>
              ),
            },
            {
              subtitle: t('privacy-policy'),
              element: (
                <RegularText>
                  https://policies.google.com/privacy?hl=en
                </RegularText>
              ),
            },
            {
              subtitle: t('cookie-name'),
              element: <RegularText>_ga,_gat,_gid</RegularText>,
            },

            {
              subtitle: t('cookie-expiration'),
              element: (
                <RegularText>{t('google-tag-manager-duration')}</RegularText>
              ),
            },
          ],
        },
        {
          id: '"mail-tracking"',
          details: [
            {
              subtitle: t('accept'),
              element: (
                <ToggleWrapper>
                  <Toggle
                    id="mail-tracking"
                    value={gdprState?.marketing?.mailTracking}
                    onChange={() => {
                      setGdprState(prevState => ({
                        ...prevState,
                        marketing: {
                          googleAnalytics:
                            gdprState?.marketing?.googleAnalytics,
                          mailTracking: !gdprState?.marketing?.mailTracking,
                        },
                      }));
                    }}
                  />
                  <RegularText>
                    {gdprState?.marketing?.mailTracking ? t('on') : t('off')}
                  </RegularText>
                </ToggleWrapper>
              ),
            },
            {
              subtitle: t('name'),
              element: <RegularText>{t('mail-tracking')}</RegularText>,
            },
            {
              subtitle: t('provider'),
              element: <RegularText>{t('website-operator')}</RegularText>,
            },
            {
              subtitle: t('purpose'),
              element: <RegularText>{t('mail-tracking-purpose')}</RegularText>,
            },
            {
              subtitle: t('privacy-policy'),
              element: (
                <RegularText>
                  {`${APP_HOST}${t('mappable-company-routes.privacy-policy-url')}`}
                </RegularText>
              ),
            },
            {
              subtitle: t('cookie-name'),
              element: <RegularText>trackingToken</RegularText>,
            },
          ],
        },
      ],
      externalMedia: [
        {
          id: 'youtube',
          details: [
            {
              subtitle: t('accept'),
              element: (
                <ToggleWrapper>
                  <Toggle
                    id="youtube"
                    value={gdprState?.externalMedia?.youtube}
                    onChange={() => {
                      setGdprState(prevState => ({
                        ...prevState,
                        externalMedia: {
                          youtube: !gdprState?.externalMedia?.youtube,
                          vimeo: gdprState?.externalMedia?.vimeo,
                        },
                      }));
                    }}
                  />
                  <RegularText>
                    {gdprState?.externalMedia?.youtube ? t('on') : t('off')}
                  </RegularText>
                </ToggleWrapper>
              ),
            },
            {
              subtitle: t('name'),
              element: <RegularText>{t('youtube')}</RegularText>,
            },
            {
              subtitle: t('provider'),
              element: (
                <RegularText>
                  Google Ireland Limited, Gordon House, Barrow Street, Dublin 4,
                  Ireland
                </RegularText>
              ),
            },
            {
              subtitle: t('purpose'),
              element: <RegularText>{t('youtube-purpose')}</RegularText>,
            },

            {
              subtitle: t('privacy-policy'),
              element: (
                <RegularText>
                  https://policies.google.com/privacy?hl=en
                </RegularText>
              ),
            },
            {
              subtitle: t('hosts'),
              element: <RegularText>google.com</RegularText>,
            },
            {
              subtitle: t('cookie-name'),
              element: <RegularText>NID</RegularText>,
            },
            {
              subtitle: t('cookie-expiration'),
              element: <RegularText>{t('youtube-duration')}</RegularText>,
            },
          ],
        },
        {
          id: 'vimeo',
          details: [
            {
              subtitle: t('accept'),
              element: (
                <ToggleWrapper>
                  <Toggle
                    id="vimeo"
                    value={gdprState?.externalMedia?.vimeo}
                    onChange={() => {
                      setGdprState(prevState => ({
                        ...prevState,
                        externalMedia: {
                          youtube: gdprState?.externalMedia?.youtube,
                          vimeo: !gdprState?.externalMedia?.vimeo,
                        },
                      }));
                    }}
                  />
                  <RegularText>
                    {gdprState?.externalMedia?.vimeo ? t('on') : t('off')}
                  </RegularText>
                </ToggleWrapper>
              ),
            },
            {
              subtitle: t('name'),
              element: <RegularText>{t('vimeo')}</RegularText>,
            },
            {
              subtitle: t('provider'),
              element: (
                <RegularText>
                  Cyberlegis-RA GmbH, Maximilianstr. 13, 80539 Munich, Germany
                </RegularText>
              ),
            },
            {
              subtitle: t('purpose'),
              element: <RegularText>{t('vimeo-purpose')}</RegularText>,
            },
            {
              subtitle: t('privacy-policy'),
              element: <RegularText>https://vimeo.com/privacy/</RegularText>,
            },
            {
              subtitle: t('hosts'),
              element: <RegularText>vimeo.com</RegularText>,
            },
            {
              subtitle: t('cookie-name'),
              element: (
                <RegularText>
                  vuid, player_clearance, cf_clearance, _cf_bm, _cf_bm
                </RegularText>
              ),
            },
            {
              subtitle: t('cookie-expiration'),
              element: <RegularText>{t('vimeo-duration')}</RegularText>,
            },
          ],
        },
      ],
    }),
    [
      gdprState?.externalMedia?.vimeo,
      gdprState?.externalMedia?.youtube,
      gdprState?.marketing?.googleAnalytics,
      gdprState?.marketing?.mailTracking,
      t,
    ]
  );

  const gdprItems = useMemo(
    () => [
      {
        title: `${t('essential')} (${gdprItemsCookies?.essential?.length})`,
        description: t('essential-description'),
        items: gdprItemsCookies?.essential,
      },
      {
        title: `${t('marketing')} (${gdprItemsCookies?.marketing?.length})`,
        description: t('marketing-description'),
        items: gdprItemsCookies?.marketing,
        checked: combineOrBooleanValues(Object.values(gdprState?.marketing)),
        onClick: () => {
          setGdprState(prevState => ({
            ...prevState,
            marketing: {
              googleAnalytics: !combineOrBooleanValues(
                Object.values(gdprState?.marketing)
              ),
              mailTracking: !combineOrBooleanValues(
                Object.values(gdprState?.marketing)
              ),
            },
          }));
        },
      },
      {
        title: `${t('external-media')} (${gdprItemsCookies?.externalMedia?.length})`,
        description: t('external-media-description'),
        items: gdprItemsCookies?.externalMedia,
        checked: combineOrBooleanValues(
          Object.values(gdprState?.externalMedia)
        ),
        onClick: () => {
          setGdprState(prevState => ({
            ...prevState,
            externalMedia: {
              youtube: !combineOrBooleanValues(
                Object.values(gdprState?.externalMedia)
              ),
              vimeo: !combineOrBooleanValues(
                Object.values(gdprState?.externalMedia)
              ),
            },
          }));
        },
      },
    ],
    [
      gdprItemsCookies?.essential,
      gdprItemsCookies?.externalMedia,
      gdprItemsCookies?.marketing,
      gdprState?.externalMedia,
      gdprState?.marketing,
      t,
    ]
  );

  if (gdprCookie && !isGdprOpen) return null;

  return (
    <GdprRoot>
      <CookieBody style={bodyProps}>
        {showGdprDetails ? (
          <PermissionView>
            <Title> {t('privacy-preference')}</Title>
            <Column $gap="0.5rem">
              <Text>{t('cookie-text-1')}</Text>
              <Text>{t('cookie-text-2')}</Text>
              <Text>{t('cookie-text-3')}</Text>
            </Column>
            <ButtonsContainer>
              <RowAlignedItems $gap="2rem">
                <Button
                  title={t('accept-all')}
                  backgroundColor={theme.color.gdprGrey2}
                  hoverTextColor={theme.color.white}
                  hoverBackgroundColor="#a3a7ae"
                  textColor={theme.color.white}
                  borderRadius={theme.borderRadius.radius5}
                  fontSize={theme.fontSize.size18}
                  lineHeight={theme.lineHeight.height150}
                  padding={isMobile ? '1.5rem 2.5rem' : '1.1rem 2rem'}
                  width={isMobile ? '100%' : 'fit-content'}
                  onClick={acceptAllGdprCookies}
                />
                <Button
                  title={t('save')}
                  backgroundColor="#f3f4f5"
                  hoverTextColor={theme.color.gdprGrey2}
                  hoverBackgroundColor={theme.color.gdprGrey}
                  textColor={theme.color.gdprGrey2}
                  borderRadius={theme.borderRadius.radius5}
                  fontSize={theme.fontSize.size18}
                  lineHeight={theme.lineHeight.height150}
                  padding={isMobile ? '1.5rem 2.5rem' : '1.1rem 2rem'}
                  width={isMobile ? '100%' : 'fit-content'}
                  onClick={onSaveGdprCookies}
                />
              </RowAlignedItems>
              <BackText onClick={onBackClick}>{t('back')}</BackText>
            </ButtonsContainer>
            <Column $gap="1rem">
              {gdprItems?.map(item => (
                <ExpandableCard
                  title={item.title}
                  description={item.description}
                  key={item.title}
                  checked={item.checked}
                  onClick={item.onClick}
                  setExpandedCard={setExpandedCard}
                  expandedCard={expandedCard}
                >
                  <Column $gap="1rem">
                    {item.items?.map(subItem => (
                      <CookieItemWrapper key={subItem.id}>
                        {subItem.details.map((detail, index) => (
                          <CookieItem
                            $lastItem={index === subItem.details?.length - 1}
                            key={detail.subtitle}
                          >
                            <RegularText>{detail.subtitle}</RegularText>
                            {detail.element}
                          </CookieItem>
                        ))}
                      </CookieItemWrapper>
                    ))}
                  </Column>
                </ExpandableCard>
              ))}
            </Column>

            <ButtonsContainer>
              <div />
              <RowAlignedItems $gap="0.5rem">
                <FooterLink
                  onClick={() => {
                    window.open(
                      `${APP_HOST}${t('mappable-company-routes.privacy-policy-url')}`,
                      '_blank'
                    );
                  }}
                >
                  {t('privacy-policy')}
                </FooterLink>
                <Divider
                  width="0.1rem"
                  height="1rem"
                  backgroundColor={theme.color.gdprGrey}
                />
                <FooterLink
                  onClick={() => {
                    window.open(
                      'https://we-conect.com/en/imprint-legal-notice/',
                      '_blank'
                    );
                  }}
                >
                  {t('imprint')}
                </FooterLink>
              </RowAlignedItems>
            </ButtonsContainer>
          </PermissionView>
        ) : (
          <ButtonView>
            <LeftSideBox>
              <Title> {t('privacy-preference')}</Title>
              <Column $gap="0.5rem">
                <Text>{t('cookie-text-1')}</Text>
                <Text>{t('cookie-text-2')}</Text>
                <Text>{t('cookie-text-3')}</Text>
                <CheckboxWrapper>
                  <Checkbox
                    label={
                      <CenterItem>
                        <SmallText>{t('essential')}</SmallText>
                      </CenterItem>
                    }
                    disabled
                    spacing={theme.spacing.size3}
                    checked={gdprState?.essential}
                    type="square"
                    size="2.5rem"
                    backgroundColor={theme.color.strokeGrey}
                    color={theme.color.mobilityGrey}
                    alignItems="start"
                    border={`0.4rem solid ${theme.color.strokeGrey}`}
                    onChange={() => {
                      setGdprState(prevState => ({
                        ...prevState,
                        essential: !gdprState?.essential,
                      }));
                    }}
                  />
                  <Checkbox
                    label={
                      <CenterItem>
                        <SmallText>{t('marketing')}</SmallText>
                      </CenterItem>
                    }
                    spacing={theme.spacing.size3}
                    checked={combineOrBooleanValues(
                      Object.values(gdprState?.marketing)
                    )}
                    type="square"
                    size="2.5rem"
                    backgroundColor={theme.color.strokeGrey}
                    color={theme.color.mobilityGrey}
                    alignItems="start"
                    border={`0.4rem solid ${theme.color.strokeGrey}`}
                    onChange={() => {
                      setGdprState(prevState => ({
                        ...prevState,
                        marketing: {
                          googleAnalytics: !combineOrBooleanValues(
                            Object.values(gdprState?.marketing)
                          ),
                          mailTracking: !combineOrBooleanValues(
                            Object.values(gdprState?.marketing)
                          ),
                        },
                      }));
                    }}
                  />
                  <Checkbox
                    label={
                      <CenterItem>
                        <SmallText>{t('external-media')}</SmallText>
                      </CenterItem>
                    }
                    spacing={theme.spacing.size3}
                    checked={combineOrBooleanValues(
                      Object.values(gdprState?.externalMedia)
                    )}
                    type="square"
                    size="2.5rem"
                    backgroundColor={theme.color.strokeGrey}
                    color={theme.color.mobilityGrey}
                    alignItems="start"
                    border={`0.4rem solid ${theme.color.strokeGrey}`}
                    onChange={() => {
                      setGdprState(prevState => ({
                        ...prevState,
                        externalMedia: {
                          youtube: !combineOrBooleanValues(
                            Object.values(gdprState?.externalMedia)
                          ),
                          vimeo: !combineOrBooleanValues(
                            Object.values(gdprState?.externalMedia)
                          ),
                        },
                      }));
                    }}
                  />
                </CheckboxWrapper>
              </Column>
            </LeftSideBox>
            <ButtonsWrapper>
              {buttonItems?.map(item => (
                <Button
                  key={item.label}
                  title={t(item.label)}
                  backgroundColor={item.backgroundColor}
                  hoverTextColor={item.hoverTextColor}
                  hoverBackgroundColor={item.hoverBackgroundColor}
                  textColor={item.textColor}
                  borderRadius={theme.borderRadius.radius5}
                  fontSize={theme.fontSize.size18}
                  lineHeight={theme.lineHeight.height150}
                  padding={isMobile ? '1.5rem 2.5rem' : '1.1rem 2rem'}
                  width="100%"
                  onClick={item.onClick}
                  testId={item.label}
                />
              ))}
              <CenterItem>
                <RowAlignedItems $gap="0.5rem">
                  <FooterLink onClick={onDetailsClick}>
                    {t('cookie-details')}
                  </FooterLink>
                  <Divider
                    width="0.1rem"
                    height="1rem"
                    backgroundColor={theme.color.gdprGrey}
                  />
                  <FooterLink
                    onClick={() => {
                      window.open(
                        `${APP_HOST}${t('mappable-company-routes.privacy-policy-url')}`,
                        '_blank'
                      );
                    }}
                  >
                    {t('privacy-policy')}
                  </FooterLink>
                  <Divider
                    width="0.1rem"
                    height="1rem"
                    backgroundColor={theme.color.gdprGrey}
                  />
                  <FooterLink
                    onClick={() => {
                      window.open(
                        'https://we-conect.com/en/imprint-legal-notice/',
                        '_blank'
                      );
                    }}
                  >
                    {t('imprint')}
                  </FooterLink>
                </RowAlignedItems>
              </CenterItem>
            </ButtonsWrapper>
          </ButtonView>
        )}
      </CookieBody>
    </GdprRoot>
  );
};

export default Gdpr;
